// Authentication hook using Zustand
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { UserResponse, LoginInput, RegisterInput, VerifyTwoFactorInput } from '../../shared/types'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface AuthState {
  user: UserResponse | null
  isLoading: boolean
  isAuthenticated: boolean
  pendingTwoFactor: {
    sessionToken: string
  } | null

  // Actions
  login: (data: LoginInput) => Promise<{ requiresTwoFactor?: boolean }>
  register: (data: RegisterInput) => Promise<void>
  logout: () => Promise<void>
  verifyTwoFactor: (code: string) => Promise<void>
  checkAuth: () => Promise<void>
  setupTwoFactor: () => Promise<{ secret: string; qrCode: string }>
  enableTwoFactor: (code: string) => Promise<void>
  disableTwoFactor: (password: string) => Promise<void>
}

async function authFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`)
  }

  return data
}

export const useAuth = create<AuthState>()(
  devtools(
    (set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  pendingTwoFactor: null,

  login: async (data) => {
    const result = await authFetch<{
      user?: UserResponse
      requiresTwoFactor?: boolean
      pendingSessionToken?: string
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    if (result.requiresTwoFactor && result.pendingSessionToken) {
      set({
        pendingTwoFactor: { sessionToken: result.pendingSessionToken },
      })
      return { requiresTwoFactor: true }
    }

    if (result.user) {
      set({
        user: result.user,
        isAuthenticated: true,
        pendingTwoFactor: null,
      })
    }

    return {}
  },

  register: async (data) => {
    const result = await authFetch<{ user: UserResponse }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    set({
      user: result.user,
      isAuthenticated: true,
    })
  },

  logout: async () => {
    await authFetch('/auth/logout', { method: 'POST' })
    set({
      user: null,
      isAuthenticated: false,
      pendingTwoFactor: null,
    })
  },

  verifyTwoFactor: async (code) => {
    const { pendingTwoFactor } = get()
    if (!pendingTwoFactor) {
      throw new Error('No pending 2FA verification')
    }

    const result = await authFetch<{ user: UserResponse }>('/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({
        code,
        sessionToken: pendingTwoFactor.sessionToken,
      }),
    })

    set({
      user: result.user,
      isAuthenticated: true,
      pendingTwoFactor: null,
    })
  },

  checkAuth: async () => {
    // Skip if already authenticated - avoid redundant API calls on navigation
    const { isAuthenticated, user } = get()
    if (isAuthenticated && user) {
      set({ isLoading: false })
      return
    }

    set({ isLoading: true })
    try {
      const result = await authFetch<{ user: UserResponse }>('/auth/me')
      set({
        user: result.user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },

  setupTwoFactor: async () => {
    const result = await authFetch<{ secret: string; qrCode: string }>(
      '/auth/2fa/setup',
      { method: 'POST' }
    )
    return result
  },

  enableTwoFactor: async (code) => {
    await authFetch('/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })

    // Update local user state
    const { user } = get()
    if (user) {
      set({ user: { ...user, twoFactorEnabled: true } })
    }
  },

  disableTwoFactor: async (password) => {
    await authFetch('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })

    // Update local user state
    const { user } = get()
    if (user) {
      set({ user: { ...user, twoFactorEnabled: false } })
    }
  },
    }),
    { name: 'AuthStore', enabled: process.env.NODE_ENV !== 'production' }
  )
)
